import { CartDrawer } from "@/components/cart/CartDrawer";
import { CartShippingAutoSelect } from "@/components/cart/CartShippingAutoSelect";
import { StickyCartBar } from "@/components/cart/StickyCartBar";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useSellerCarts } from "@/queries/cart";
import { useFavoritesListId } from "@/queries/customer/favorites";
import { formatSavings } from "@/queries/orders/order-insights";
import { useCustomerOrderStats } from "@/queries/orders/orders";
import { OrderAssistant } from "@/components/order-assistant";
import { WhatsAppSupportButton } from "@/components/support/WhatsAppSupportButton";
import { VoiceOrderFab } from "@/components/voice-order";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { useEffect, useMemo, useState } from "react";
import { ActionSearchBar, MobileSearchButton } from "./action-search-bar";
import { AppHeader } from "./AppHeader";
import {
  Sidebar,
  SidebarBody,
  SidebarLink,
  type SidebarLinkItem
} from "./sidebar";

// Stable empty list so the memoized shipping watcher is not re-rendered by a
// fresh `[]` on every layout render while the carts are still loading.
const NO_CARTS: SalonStockerHttpTypes.StoreSellerCart[] = [];

type Props = {
  links: SidebarLinkItem[];
  profile: SalonStockerHttpTypes.StoreProfile;
};

export function MainLayout({
  children,
  links,
  profile
}: React.PropsWithChildren<Props>) {
  const [open, setOpen] = useState<boolean>(false);
  const [cartOpen, setCartOpen] = useState<boolean>(false);

  // Where the cart lives at this width. Three answers, one ladder:
  //   2xl+       - the rail on the search page; the header button is a link to /cart
  //   md/lg/xl   - no rail, so the header button opens the slide-over
  //   <md        - no rail and no slide-over; the sticky bar and /cart carry it
  // See storefront#145 (P4).
  const hasCartRail = useBreakpoint("2xl");
  const cartOpensDrawer = useBreakpoint("md") && !hasCartRail;

  // Crossing out of the slide-over's range with it open would otherwise leave
  // `cartOpen` set, and the drawer would spring back the moment the viewport
  // returned - after the buyer had done something else entirely.
  useEffect(() => {
    if (!cartOpensDrawer) {
      setCartOpen(false);
    }
  }, [cartOpensDrawer]);

  // Fetch real-time cart data
  const { data: cartData } = useSellerCarts();
  const cartCount = cartData?.summary?.total_items ?? 0;
  const cartTotal = cartData?.summary?.grand_total ?? 0;

  const favoritesListId = useFavoritesListId();

  // The same stat, from the same query, as the dashboard's Savings tile, so the
  // two cannot disagree. Unknown (loading or failed) shows no chip at all, and
  // neither does a salon that has saved nothing yet: "$0.00 saved" in the
  // header of a new salon is noise (the dashboard tile still says $0.00).
  // The chip only shows from `xl` up, so below it the query is left to whoever
  // else needs it (the dashboard tile fetches at every width).
  const showsSavedChip = useBreakpoint("xl");
  const { data: orderStats } = useCustomerOrderStats({
    enabled: showsSavedChip
  });
  const savedLabel = orderStats?.totalSaved
    ? formatSavings(orderStats)
    : undefined;

  const menuItems = useMemo(() => {
    return links.map((link) => <SidebarLink key={link.label} link={link} />);
  }, [links]);

  const searchBar = useMemo(() => {
    return <ActionSearchBar />;
  }, []);

  // The phone header's search: an icon, not the full bar (which shows from md).
  const mobileSearch = useMemo(() => <MobileSearchButton />, []);

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-gray-100 md:flex-row dark:bg-neutral-800">
      {/* Sidebar - Full height. Below `md` this renders only the slide-in nav
          drawer; its trigger moved into the header's phone row, so the shell
          isn't spending a whole band on a logo and a hamburger. */}
      <Sidebar open={open} setOpen={setOpen}>
        <SidebarBody>
          <div className="scrollbar-hide scrollbar-show-hover flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
            <div className="flex flex-col gap-2">{menuItems}</div>
          </div>
        </SidebarBody>
      </Sidebar>

      {/* Main content area with header */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <AppHeader
          searchBar={searchBar}
          mobileSearch={mobileSearch}
          cartCount={cartCount}
          cartTotal={cartTotal}
          savedLabel={savedLabel}
          userName={profile.first_name + " " + profile.last_name}
          salonName={profile.company_name}
          onOpenNav={() => setOpen(true)}
          onOpenCart={cartOpensDrawer ? () => setCartOpen(true) : undefined}
          favoritesListId={favoritesListId}
        />

        {/* Page Content */}
        <div className="custom-scrollbar flex-1 overflow-y-auto">
          {children}
        </div>

        {/* Phone-only cart bar, outside the scroll container so it stays put. */}
        <StickyCartBar />
      </div>

      {/* The cart slide-over exists only in the band that has no rail - so a
          desktop session never mounts a second copy of the cart, and a phone
          never mounts one it can't open. */}
      {cartOpensDrawer && (
        <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
      )}

      <CartShippingAutoSelect carts={cartData?.carts ?? NO_CARTS} />

      <WhatsAppSupportButton profile={profile} />
      <VoiceOrderFab />
      <OrderAssistant customerId={profile.id} />
    </div>
  );
}
